import React, { useState, useEffect } from 'react';
import adminApi from '../../api/client';

interface Publisher {
  id: string;
  name_en: string | null;
  name_si: string | null;
  linked_books_count: number;
}

export default function MergeToolPublishers() {
  const [sourceSearch, setSourceSearch] = useState('');
  const [debouncedSource, setDebouncedSource] = useState('');
  const [sourceResults, setSourceResults] = useState<Publisher[]>([]);
  const [selectedSource, setSelectedSource] = useState<Publisher | null>(null);

  const [targetSearch, setTargetSearch] = useState('');
  const [debouncedTarget, setDebouncedTarget] = useState('');
  const [targetResults, setTargetResults] = useState<Publisher[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<Publisher | null>(null);

  const [isMerging, setIsMerging] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mergeResult, setMergeResult] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSource(sourceSearch), 400);
    return () => clearTimeout(handler);
  }, [sourceSearch]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedTarget(targetSearch), 400);
    return () => clearTimeout(handler);
  }, [targetSearch]);

  const searchPublishers = async (q: string, setResults: React.Dispatch<React.SetStateAction<Publisher[]>>) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    try {
      const res = await adminApi.get(`/admin/master/publishers?q=${q}&per_page=8`);
      setResults(res.data.items || []);
    } catch (err) {
      console.error("Search failed", err);
    }
  };

  useEffect(() => {
    if (!selectedSource) searchPublishers(debouncedSource, setSourceResults);
  }, [debouncedSource, selectedSource]);

  useEffect(() => {
    if (!selectedTarget) searchPublishers(debouncedTarget, setTargetResults);
  }, [debouncedTarget, selectedTarget]);

  const handleMerge = async () => {
    if (!selectedSource || !selectedTarget) return;
    setIsMerging(true);
    setMergeResult(null);
    try {
      const payload = { source_id: selectedSource.id, target_id: selectedTarget.id };
      const res = await adminApi.post('/admin/master/publishers/merge', payload);
      const data = res.data;
      const targetName = selectedTarget.name_en || selectedTarget.name_si || 'Target';
      setMergeResult({
        type: 'success',
        message: `✅ Merged. ${data.books_repointed} books and ${data.sinhala_books_repointed} Sinhala books re-pointed to '${targetName}'. Source deactivated.`
      });
      setSelectedSource(null);
      setSourceSearch('');
      setSourceResults([]);
      setSelectedTarget(null);
      setTargetSearch('');
      setTargetResults([]);
      setShowConfirm(false);
    } catch (err: any) {
      setMergeResult({
        type: 'error',
        message: err.response?.data?.detail || 'Merge failed'
      });
      setShowConfirm(false);
    } finally {
      setIsMerging(false);
    }
  };

  const isSameRecord = selectedSource && selectedTarget && selectedSource.id === selectedTarget.id;
  const canMerge = selectedSource && selectedTarget && !isSameRecord;

  const renderSearchBox = (
    label: string,
    search: string,
    setSearch: (s: string) => void,
    results: Publisher[],
    selected: Publisher | null,
    setSelected: (p: Publisher | null) => void,
    isSource: boolean
  ) => {
    return (
      <div className="flex-1 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h4 className="font-bold text-[#085041] mb-4">{label}</h4>
        
        {selected ? (
          <div className={`p-4 rounded border ${isSource ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium text-gray-900">{selected.name_en || '—'}</div>
                <div className="text-sm text-gray-500">{selected.name_si || '—'}</div>
              </div>
              <button 
                onClick={() => { setSelected(null); setSearch(''); }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                ✕ change
              </button>
            </div>
            <span className="inline-block bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 font-medium">
              {selected.linked_books_count} books
            </span>
          </div>
        ) : (
          <div>
            <input
              type="text"
              placeholder="Search publisher..."
              className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75] mb-2"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            
            {search.trim() && results.length === 0 && (
              <div className="text-sm text-gray-500 p-2 text-center">No results found.</div>
            )}

            {results.length > 0 && (
              <ul className="border border-gray-200 rounded divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {results.map((publisher) => (
                  <li 
                    key={publisher.id} 
                    className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                    onClick={() => setSelected(publisher)}
                  >
                    <div>
                      <div className="font-medium text-sm text-gray-900">{publisher.name_en || '—'}</div>
                      <div className="text-xs text-gray-500">{publisher.name_si || '—'}</div>
                    </div>
                    <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs whitespace-nowrap ml-2">
                      {publisher.linked_books_count} books
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  const getDisplayName = (p: Publisher) => p.name_en || p.name_si || 'Publisher';

  return (
    <div className="space-y-6">
      {mergeResult && (
        <div className={`p-4 rounded border ${mergeResult.type === 'success' ? 'bg-[#1D9E75]/10 border-[#1D9E75] text-[#085041]' : 'bg-[#D85A30]/10 border-[#D85A30] text-[#D85A30]'}`}>
          {mergeResult.message}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6">
        {renderSearchBox(
          "Source (duplicate — will be deactivated)", 
          sourceSearch, setSourceSearch, sourceResults, 
          selectedSource, setSelectedSource, true
        )}
        
        <div className="flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 rotate-90 md:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>

        {renderSearchBox(
          "Target (canonical — keeps all references)", 
          targetSearch, setTargetSearch, targetResults, 
          selectedTarget, setSelectedTarget, false
        )}
      </div>

      <div className="flex flex-col items-center mt-8">
        {isSameRecord && (
          <div className="text-[#D85A30] text-sm mb-3 font-medium">Source and target must be different records.</div>
        )}
        
        <button
          disabled={!canMerge}
          onClick={() => setShowConfirm(true)}
          className={`px-8 py-3 rounded font-bold text-white transition-opacity ${canMerge ? 'bg-[#D85A30] hover:bg-opacity-90' : 'bg-gray-300 cursor-not-allowed'}`}
        >
          Merge Publishers
        </button>
      </div>

      {showConfirm && selectedSource && selectedTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-bold text-[#D85A30] mb-4">Confirm Merge</h3>
            <p className="text-gray-800 mb-4 font-medium">
              Merge '{getDisplayName(selectedSource)}' into '{getDisplayName(selectedTarget)}'?
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600 mb-6">
              <li>All books currently linked to '{getDisplayName(selectedSource)}' will be re-pointed to '{getDisplayName(selectedTarget)}'</li>
              <li>'{getDisplayName(selectedSource)}' will be deactivated (not deleted) and hidden from search</li>
              <li>This action cannot be easily undone</li>
            </ul>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setShowConfirm(false)} 
                disabled={isMerging}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleMerge}
                disabled={isMerging}
                className="px-4 py-2 bg-[#D85A30] text-white rounded hover:bg-opacity-90 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isMerging ? 'Merging...' : 'Confirm Merge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
